import React, { useRef, useState, useCallback } from "react";

const finePointer = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches &&
  !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Subtle interactive 3D tilt on hover, driven by cursor position.
 * Animates only `transform` (GPU-friendly). Falls back to a plain wrapper
 * on touch devices or when reduced-motion is preferred.
 */
export const TiltCard = ({ children, className = "", max = 8, scale = 1.02, glare = true }) => {
  const ref = useRef(null);
  const [enabled] = useState(finePointer);
  const [t, setT] = useState({ rx: 0, ry: 0, active: false });
  const [g, setG] = useState({ x: 50, y: 50 });

  const onMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    setT({ rx: (0.5 - py) * max * 2, ry: (px - 0.5) * max * 2, active: true });
    setG({ x: px * 100, y: py * 100 });
  }, [max]);

  const onLeave = useCallback(() => setT({ rx: 0, ry: 0, active: false }), []);

  if (!enabled) return <div className={className}>{children}</div>;

  return (
    <div style={{ perspective: "900px" }} className={className}>
      <div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        className="relative h-full"
        style={{
          transform: `rotateX(${t.rx}deg) rotateY(${t.ry}deg) scale(${t.active ? scale : 1})`,
          transition: t.active ? "transform 80ms ease-out" : "transform 400ms cubic-bezier(0.2,0.8,0.2,1)",
          transformStyle: "preserve-3d",
          willChange: "transform",
        }}
      >
        {children}
        {glare && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-[inherit] transition-opacity duration-200"
            style={{
              opacity: t.active ? 1 : 0,
              background: `radial-gradient(340px circle at ${g.x}% ${g.y}%, rgba(223,200,120,0.14), transparent 60%)`,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default TiltCard;
